<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import {
  type Bill,
  BillStatus,
  BillingPeriod,
  type BillingProfile,
} from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import AdvancedTitle from 'gpcore-uikit/components/Component.AdvancedTitle.vue';
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  type IconDefinition,
  faArrowRight,
  faCheck,
  faCircle,
  faClock,
  faFileArrowDown,
  faFileInvoiceDollar,
  faQuestion,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import SinglePageLayout from '@panel/layouts/Layout.SinglePage.vue';
import useBillStatus from '@panel/components/user/billing-profiles/table-bills/composables/useBillStatus';
import ComponentStatus from 'gpcore-uikit/components/Component.Status.vue';
import { BillingProfileInject } from '@panel/components/user/billing-profiles/symbols';
import AddressField from '@panel/components/user/billing-profiles/address/AddressField.vue';
import useIssuedAtDate from '@panel/components/user/billing-profiles/table-bills/composables/useIssuedAtDate';
import { type ProjectNodeUsage, UsageUnitType } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import {
  GpBrandMessage,
  GpBtn,
  GpTable,
  GpTableColumn,
  type GpTableDataRow,
  GpTag,
} from '@gportal/uikit';
import { toImageSrc } from 'gpcore-apikit/lib/fileHelper';
import getEnumLabel from 'gpcore-apikit/lib/getEnumLabel';
import useUserViewTitle from '@panel/composables/useUserViewTitle';
import Breadcrumbs from 'gpcore-uikit/components/breadcrumbs/Breadcrumbs.vue';
import BillDetailsLine from './BillDetailsLine.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

useUserViewTitle('Bill');

interface UsagesTableRow extends GpTableDataRow {
  flavourName: string;
  billingPeriod: string;
  nodeUsages: Array<ProjectNodeUsage>;
  units: number;
  unitType: string;
  priceTotal: string;
}

const billingProfile = ref<BillingProfile>();
provide(BillingProfileInject, billingProfile);

const bill = ref<Bill>();
const usages = ref<Array<UsagesTableRow>>();
const userStore = useUserStore();
const route = useRoute();
const isLoading = ref(false);
const { addNotification } = useNotifications();

const getBillingPeriod = (period: BillingPeriod) => getEnumLabel(BillingPeriod, period);

const getUnitType = (period: UsageUnitType) => getEnumLabel(UsageUnitType, period);

const reloadBill = async () => {
  isLoading.value = true;
  try {
    const response = await userStore.getBillDetails({
      billingProfileId: route.params.billingProfileId as string,
      billId: route.params.billId as string,
    });
    bill.value = response.bill;

    usages.value = response.usage.flavourUsages.map((fl) => ({
      key: fl.flavour.id,
      flavourName: fl.flavour.name,
      nodeUsages: fl.nodeUsages,
      billingPeriod: getBillingPeriod(fl.period),
      units: fl.units,
      unitType: getUnitType(fl.unitType),
      priceTotal: fl.total.formatted,
    }));

    billingProfile.value = response.bill.billingProfile;
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isLoading.value = false;
  }
};

const { getStatus } = useBillStatus();
const formatDate = useIssuedAtDate();

const billStatusIcon = computed<{ icon: IconDefinition; class: string[] }>(() => {
  switch (bill.value.status) {
    case BillStatus.FAILED:
      return {
        icon: faTimes,
        class: [/* tw */ 'tw-text-gp-fire'],
      };
    case BillStatus.SUCCEEDED:
      return {
        icon: faCheck,
        class: [/* tw */ 'tw-text-gp-cyan'],
      };
    case BillStatus.PENDING:
      return {
        icon: faClock,
        class: [/* tw */ 'tw-text-gp-orange'],
      };
    default:
      return {
        icon: faQuestion,
        class: [/* tw */ 'tw-text-gp-rhino'],
      };
  }
});

const isDownloadingBill = ref(false);
const downloadBill = async () => {
  isDownloadingBill.value = true;

  try {
    const response = await userStore.downloadBillPdf({
      billId: bill.value.id,
      billingProfileId: billingProfile.value.id,
    });
    const source = toImageSrc(response.pdf);

    const downloadLink = document.createElement('a');
    downloadLink.href = source;
    downloadLink.download = bill.value.number;
    downloadLink.click();
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isDownloadingBill.value = false;
  }
};

onMounted(() => {
  reloadBill();
});
</script>

<template>
  <SinglePageLayout>
    <template #breadcrumb>
      <Breadcrumbs
        :hide-home-route="true"
        :items="[
          {
            label: 'Billing profiles',
            to: {
              name: 'UserBillingProfiles',
              query: {
                billingProfile: billingProfile?.id,
              },
            },
          },
          {
            label: 'Bill',
          },
        ]"
      />
    </template>

    <template #content>
      <template v-if="!isLoading && bill != null">
        <AdvancedTitle class="tw-mt-8" :title="bill.number" pretitle="Bill" :subtitle="bill.id">
          <template #icon>
            <div class="tw-relative tw-aspect-square tw-h-12 md:tw-h-24">
              <FontAwesomeIcon
                class="tw-align-[1rem] tw-text-6xl md:tw-text-[6rem]"
                :icon="faFileInvoiceDollar"
              />
              <span
                class="fa-stack fa-2x tw-absolute tw-bottom-0 tw-right-0 -tw-mb-6 -tw-mr-4 tw-text-base md:-tw-mb-1 md:tw-mr-1"
              >
                <FontAwesomeIcon
                  class="tw-align-middle"
                  :icon="faCircle"
                  :class="['fa-stack-2x', ...billStatusIcon.class]"
                />
                <FontAwesomeIcon
                  class="fa-stack-1x tw-align-middle tw-text-black"
                  :icon="billStatusIcon.icon"
                />
              </span>
            </div>
          </template>
        </AdvancedTitle>
        <div class="tw-my-4 tw-flex tw-justify-end">
          <GpBtn
            label="Download"
            :icon="faFileArrowDown"
            size="sm"
            @click="downloadBill"
            :disabled="isDownloadingBill"
          />
        </div>
        <div class="tw-mb-8 tw-flex">
          <div class="tw-mb-4 tw-w-full md:tw-w-1/2">
            <AddressField :editable="false" />
          </div>
          <div class="tw-w-full md:tw-w-1/2">
            <h3 class="tw-mb-4">Info</h3>
            <p class="tw-mb-2 tw-flex">
              <label class="tw-w-1/3 tw-font-bold tw-uppercase tw-text-gp-rhino">Status</label>
              <span class="tw-w-2/3">
                <ComponentStatus v-bind="getStatus(bill.status)" />
              </span>
            </p>
            <p class="tw-mb-2 tw-flex">
              <label class="tw-w-1/3 tw-font-bold tw-uppercase tw-text-gp-rhino">Issued at</label>
              <span class="tw-w-2/3">{{ formatDate(bill.issuedAt) }}</span>
            </p>
          </div>
        </div>
        <h3 class="tw-mb-4">Usage</h3>
        <GpTable v-if="usages.length > 0" :data="usages">
          <GpTableColumn field="flavourName" label="Flavour" :header-style="{ width: '50%' }">
            <template
              #body="{ data, row }: { data: UsagesTableRow['flavourName']; row: UsagesTableRow }"
            >
              <p class="tw-text-bold">
                <span class="tw-text-xl">{{ data }}</span>
                <GpTag class="tw-ml-2" size="xs">
                  {{ row.billingPeriod }}
                </GpTag>
              </p>
              <p v-for="nodeUsage in row.nodeUsages" :key="nodeUsage.id">
                {{ nodeUsage.name }}
                <br />
                {{ formatDate(nodeUsage.startedAt) }} &nbsp;<FontAwesomeIcon
                  :icon="faArrowRight"
                />&nbsp;
                {{ formatDate(nodeUsage.endedAt) }}
              </p>
            </template>
          </GpTableColumn>
          <GpTableColumn
            field="units"
            label="Units"
            alignment="right"
            vertical-alignment="top"
            header-class="tw-w-1/4"
          >
            <template #body="{ data, row }"> {{ data }} {{ row.unitType.toLowerCase() }} </template>
          </GpTableColumn>
          <GpTableColumn
            field="priceTotal"
            label="Price"
            alignment="right"
            vertical-alignment="top"
            column-class="tw-font-bold"
            header-class="tw-w-1/4"
          />
        </GpTable>
        <GpBrandMessage
          v-else
          class="tw-mx-auto tw-mb-8"
          message="No usages found for this bill."
        />

        <div
          class="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-y-4 tw-border-t tw-border-gp-rhino tw-pt-4 md:tw-grid-cols-4"
        >
          <!-- net price -->
          <BillDetailsLine>
            <template #label>Sub total (excl. tax)</template>
            <template #value>{{ bill.netPrice.formatted }}</template>
          </BillDetailsLine>

          <!-- tax -->
          <BillDetailsLine>
            <template #label>Tax rate</template>
            <template #value>{{ bill.taxPrice.formatted }}</template>
          </BillDetailsLine>

          <!-- gross price -->
          <BillDetailsLine>
            <template #label>Sub total (incl. tax)</template>
            <template #value>{{ bill.grossPrice.formatted }}</template>
          </BillDetailsLine>

          <!-- credit -->
          <template v-if="bill.creditPrice.amount > 0">
            <BillDetailsLine>
              <template #label>Credit</template>
              <template #value>- {{ bill.creditPrice.formatted }}</template>
            </BillDetailsLine>
          </template>

          <hr class="tw-col-span-2 tw-border-gp-rhino md:tw-col-start-3 md:tw-col-end-5" />

          <!-- total -->
          <BillDetailsLine>
            <template #label>
              <span class="tw-font-bold">TOTAL</span>
            </template>
            <template #value>
              <span class="tw-text-gp-cyan">{{ bill.totalPrice.formatted }}</span>
            </template>
          </BillDetailsLine>
        </div>
      </template>
    </template>
  </SinglePageLayout>
</template>
